import { VehicleMapDataPoint } from "../../../api/types/VehicleMap";
import { eventIconUrl } from "./eventIcons";

export type StackedEventRow = {
  // identifies the pin behind this row back in the renderer
  id: string;
  point: VehicleMapDataPoint;
  label: string;
  time: string;
  detail?: string;
};

// The events hidden under a stack badge, listed out so each one can be
// reached. Same shape and manners as createClusterAssetListOverlay - that
// pattern is already on the fleet map for overlapping routes, so there is
// nothing new to learn here; this one lists events at a position rather
// than routes along a road.
// the pin's artwork runs this far above the position it marks, so the list
// has to clear that rather than the position itself
const PIN_HEIGHT_ABOVE_ANCHOR = 24;
const GAP = 10;
const PADDING = 8;
// half a pin plus a little, for the alongside placement
const SIDE_CLEARANCE = 16;

export const createEventStackListOverlay = ({
  google,
  map,
  position,
  rows,
  onHoverEvent,
  onSelectEvent,
  onPointerEnter,
  onPointerLeave,
  onClose,
}: {
  google: any;
  map: any;
  position: { lat: number; lng: number };
  rows: StackedEventRow[];
  onHoverEvent: (id: string | null) => void;
  onSelectEvent: (id: string) => void;
  // the list opens on hover, so it has to know when the pointer is over it
  // - otherwise leaving the pin to reach a row closes it on the way
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onClose: () => void;
}) => {
  class EventStackListOverlay extends google.maps.OverlayView {
    container: HTMLDivElement | null = null;

    onAdd() {
      const container = document.createElement("div");
      container.style.cssText = `
        position: absolute;
        width: min(300px, calc(100vw - 24px));
        max-height: min(260px, calc(100vh - 24px));
        background: #ffffff;
        border-radius: 10px;
        box-shadow: 0 6px 24px rgba(0,0,0,0.22);
        overflow: hidden;
        font-family: inherit;
      `;
      // the badge is clickable and sits under this, so clicks must not fall
      // through to the map's own close-on-click handler
      container.addEventListener("click", (event) => event.stopPropagation());
      container.addEventListener("mousedown", (event) =>
        event.stopPropagation()
      );
      container.addEventListener("mouseenter", onPointerEnter);
      container.addEventListener("mouseleave", onPointerLeave);

      const header = document.createElement("div");
      header.style.cssText = `
        display: flex; align-items: center; justify-content: space-between;
        gap: 8px; padding: 8px 10px; border-bottom: 1px solid #eef0f3;
      `;
      const title = document.createElement("span");
      title.textContent = `${rows.length} events here`;
      title.style.cssText = "font-size: 12px; font-weight: 700; color: #1f2937;";
      const closeButton = document.createElement("button");
      closeButton.type = "button";
      closeButton.setAttribute("aria-label", "Close");
      closeButton.textContent = "×";
      closeButton.style.cssText = `
        border: 0; background: none; cursor: pointer; line-height: 1;
        font-size: 17px; color: #9aa1ab; padding: 0 2px;
      `;
      closeButton.addEventListener("click", onClose);
      header.append(title, closeButton);

      const list = document.createElement("div");
      list.style.cssText =
        "max-height: min(212px, calc(100vh - 112px)); overflow-y: auto;";

      rows.forEach((row) => {
        const rowEl = document.createElement("div");
        rowEl.style.cssText = `
          display: flex; align-items: center; gap: 8px;
          padding: 7px 10px; cursor: pointer;
          border-bottom: 1px solid #f4f5f7;
        `;
        rowEl.addEventListener("mouseenter", () => {
          rowEl.style.background = "#f3f6fb";
          onHoverEvent(row.id);
        });
        rowEl.addEventListener("mouseleave", () => {
          rowEl.style.background = "transparent";
          onHoverEvent(null);
        });
        rowEl.addEventListener("click", () => onSelectEvent(row.id));

        const icon = document.createElement("img");
        icon.src = eventIconUrl(row.point, 18);
        icon.alt = "";
        icon.style.cssText = "width: 18px; height: 18px; flex-shrink: 0;";

        const text = document.createElement("span");
        text.style.cssText = `
          flex: 1 1 auto; min-width: 0; font-size: 12px; color: #1f2937;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        `;
        text.textContent = row.detail
          ? `${row.label} · ${row.detail}`
          : row.label;
        text.title = text.textContent;

        const time = document.createElement("span");
        time.style.cssText =
          "font-size: 11px; color: #6b7280; flex-shrink: 0; font-variant-numeric: tabular-nums;";
        time.textContent = row.time;

        rowEl.append(icon, text, time);
        list.appendChild(rowEl);
      });

      container.append(header, list);
      this.container = container;
      this.getPanes().floatPane.appendChild(container);
    }

    draw() {
      if (!this.container) return;
      const point = this.getProjection()?.fromLatLngToDivPixel(
        new google.maps.LatLng(position.lat, position.lng)
      );
      if (!point) return;
      const mapDiv = map.getDiv();
      const width = this.container.offsetWidth;
      const height = this.container.offsetHeight;
      const mapWidth = mapDiv.clientWidth;
      const mapHeight = mapDiv.clientHeight;
      const clamp = (value: number, max: number) =>
        Math.min(Math.max(value, PADDING), Math.max(PADDING, max));

      // Above the stack, below it, or beside it - whichever fits. Picking
      // one and clamping it into view is what used to leave the list
      // stranded a long way from the pin it belongs to: a stack low on the
      // map had nowhere below to go, and the clamp slid the whole panel up
      // to the top of the map rather than putting it somewhere adjacent.
      const aboveTop = point.y - PIN_HEIGHT_ABOVE_ANCHOR - GAP - height;
      const belowTop = point.y + GAP;
      let left: number;
      let top: number;
      if (aboveTop >= PADDING) {
        top = aboveTop;
        left = point.x - width / 2;
      } else if (belowTop + height <= mapHeight - PADDING) {
        top = belowTop;
        left = point.x - width / 2;
      } else {
        // too tall for either: sit alongside, level with the pin
        top = point.y - height / 2;
        left = point.x + GAP + SIDE_CLEARANCE;
        if (left + width > mapWidth - PADDING)
          left = point.x - GAP - SIDE_CLEARANCE - width;
      }

      this.container.style.left = `${clamp(left, mapWidth - width - PADDING)}px`;
      this.container.style.top = `${clamp(top, mapHeight - height - PADDING)}px`;
    }

    onRemove() {
      onHoverEvent(null);
      this.container?.parentNode?.removeChild(this.container);
      this.container = null;
    }
  }

  return new EventStackListOverlay();
};
